/**
 * Recommendation Context
 *
 * Provides centralized recommendation state management including:
 * - Recommendation fetching with caching
 * - Client-side filtering and sorting
 * - Optimistic updates for filter operations
 * - Session-based recommendation management
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { recommendationService } from '../services/api';
import type { ApiError, Product, RecommendationScore } from '../services/api/types';

/**
 * Combined recommendation with product data
 */
export interface EnrichedRecommendation {
  productId: string;
  product: Product | null;
  score: number;
  confidence: number;
  reasoning: string[];
}

/**
 * Filter state for recommendations
 */
export interface RecommendationFilters {
  categories: string[];
  priceRange: {
    min: number | null;
    max: number | null;
  };
  minScore: number | null;
  attributes: Record<string, unknown>;
}

/**
 * Sort order options
 */
export type RecommendationSortOrder =
  | 'score-desc'
  | 'score-asc'
  | 'price-asc'
  | 'price-desc'
  | 'name-asc'
  | 'name-desc';

/**
 * Recommendation context value
 */
export interface RecommendationContextValue {
  // Data state
  recommendations: EnrichedRecommendation[];
  filteredRecommendations: EnrichedRecommendation[];
  allProducts: Product[];

  // Filter and sort state
  filters: RecommendationFilters;
  sortOrder: RecommendationSortOrder;

  // UI state
  loading: boolean;
  error: ApiError | null;

  // Actions
  fetchRecommendations: (sessionId: string) => Promise<void>;
  applyFilter: (filters: Partial<RecommendationFilters>) => void;
  updateSortOrder: (order: RecommendationSortOrder) => void;
  clearFilters: () => void;
  clearError: () => void;

  // Computed properties
  hasActiveFilters: boolean;
  recommendationCount: number;
  filteredCount: number;
}

/**
 * Default filter state
 */
const defaultFilters: RecommendationFilters = {
  categories: [],
  priceRange: {
    min: null,
    max: null,
  },
  minScore: null,
  attributes: {},
};

/**
 * Type guard for ApiError
 */
function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error
  );
}

/**
 * Convert unknown error to ApiError
 */
function toApiError(error: unknown): ApiError {
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
    };
  }

  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
  };
}

/**
 * Apply filters to recommendations
 */
function applyFiltersToRecommendations(
  recommendations: EnrichedRecommendation[],
  filters: RecommendationFilters
): EnrichedRecommendation[] {
  return recommendations.filter((rec) => {
    if (!rec.product) return false;

    // Category filter
    if (filters.categories.length > 0) {
      const productCategory =
        typeof rec.product.attributes.category === 'string'
          ? rec.product.attributes.category
          : '';
      if (!filters.categories.includes(productCategory)) {
        return false;
      }
    }

    // Price range filter
    const productPrice = rec.product.price ?? 0;
    if (filters.priceRange.min !== null && productPrice < filters.priceRange.min) {
      return false;
    }
    if (filters.priceRange.max !== null && productPrice > filters.priceRange.max) {
      return false;
    }

    // Score filter
    if (filters.minScore !== null && rec.score < filters.minScore) {
      return false;
    }

    // Attribute filters
    for (const [key, value] of Object.entries(filters.attributes)) {
      if (value === null || value === undefined) continue;

      const productAttrValue = rec.product.attributes[key];

      // Handle array values (multi-select)
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        if (!value.includes(productAttrValue)) {
          return false;
        }
      } else if (productAttrValue !== value) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Sort recommendations
 */
function sortRecommendations(
  recommendations: EnrichedRecommendation[],
  sortOrder: RecommendationSortOrder
): EnrichedRecommendation[] {
  const sorted = [...recommendations];

  switch (sortOrder) {
    case 'score-desc':
      sorted.sort((a, b) => b.score - a.score);
      break;
    case 'score-asc':
      sorted.sort((a, b) => a.score - b.score);
      break;
    case 'price-asc':
      sorted.sort((a, b) => {
        const priceA = a.product?.price ?? 0;
        const priceB = b.product?.price ?? 0;
        return priceA - priceB;
      });
      break;
    case 'price-desc':
      sorted.sort((a, b) => {
        const priceA = a.product?.price ?? 0;
        const priceB = b.product?.price ?? 0;
        return priceB - priceA;
      });
      break;
    case 'name-asc':
      sorted.sort((a, b) => {
        const nameA = a.product?.name ?? '';
        const nameB = b.product?.name ?? '';
        return nameA.localeCompare(nameB);
      });
      break;
    case 'name-desc':
      sorted.sort((a, b) => {
        const nameA = a.product?.name ?? '';
        const nameB = b.product?.name ?? '';
        return nameB.localeCompare(nameA);
      });
      break;
  }

  return sorted;
}

/**
 * Recommendation Context
 */
const RecommendationContext = createContext<RecommendationContextValue | undefined>(undefined);

/**
 * Recommendation Provider Props
 */
export interface RecommendationProviderProps {
  children: ReactNode;
}

/**
 * Recommendation Provider Component
 */
export const RecommendationProvider: React.FC<RecommendationProviderProps> = ({ children }) => {
  const [recommendations, setRecommendations] = useState<EnrichedRecommendation[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [filters, setFilters] = useState<RecommendationFilters>(defaultFilters);
  const [sortOrder, setSortOrder] = useState<RecommendationSortOrder>('score-desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // Cache for recommendation responses
  const [recommendationCache] = useState<Map<string, EnrichedRecommendation[]>>(new Map());

  /**
   * Fetch recommendations for a quiz session
   */
  const fetchRecommendations = useCallback(
    async (sessionId: string): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cached = recommendationCache.get(sessionId);
        if (cached) {
          setRecommendations(cached);
          setLoading(false);
          return;
        }

        // Generate recommendations
        const recommendationsResponse = await recommendationService.generateRecommendations({
          sessionId,
        });

        if (!recommendationsResponse.success || !recommendationsResponse.data) {
          throw recommendationsResponse.error || new Error('Failed to generate recommendations');
        }

        const recommendationScores: RecommendationScore[] =
          recommendationsResponse.data.recommendations || [];

        // Early return if no recommendations
        if (recommendationScores.length === 0) {
          setRecommendations([]);
          setAllProducts([]);
          setLoading(false);
          return;
        }

        // Transform recommendations directly - product data is already in the response
        const enrichedRecommendations: EnrichedRecommendation[] = recommendationScores.map(
          (rec: any) => {
            // Extract product data from recommendation response
            const product: Product = {
              id: rec.productId,
              tenantId: '', // Not provided in recommendation response
              name: rec.name || '',
              description: rec.description,
              price: rec.attributes?.price ?? 0,
              currency: rec.attributes?.currency || 'EUR',
              imageUrl: rec.imageUrl || rec.attributes?.imageUrl || null,
              attributes: {
                ...rec.attributes,
                category: rec.category, // Store category in attributes
              },
              createdAt: new Date().toISOString(), // Not provided in recommendation response
              updatedAt: new Date().toISOString(), // Not provided in recommendation response
            };

            return {
              productId: rec.productId,
              product,
              score: Math.min(100, Math.max(0, rec.scoreBreakdown?.normalizedScore ?? rec.score)),
              confidence: Math.min(1, Math.max(0, (rec.scoreBreakdown?.normalizedScore ?? Math.min(100, rec.score)) / 100)),
              reasoning: rec.matchedRules || rec.reasoning || [],
            };
          }
        );

        // Extract products for allProducts state
        const products = enrichedRecommendations
          .map((rec) => rec.product)
          .filter((product): product is Product => product !== null);

        // Cache recommendations
        recommendationCache.set(sessionId, enrichedRecommendations);

        // Update state - no filtering needed, all products have data
        setRecommendations(enrichedRecommendations);
        setAllProducts(products);
        setLoading(false);
      } catch (err) {
        const apiError = toApiError(err);
        setError(apiError);
        setLoading(false);
      }
    },
    [recommendationCache]
  );

  /**
   * Apply filters (optimistic update)
   */
  const applyFilter = useCallback((newFilters: Partial<RecommendationFilters>): void => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  /**
   * Update sort order (optimistic update)
   */
  const updateSortOrder = useCallback((order: RecommendationSortOrder): void => {
    setSortOrder(order);
  }, []);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback((): void => {
    setFilters(defaultFilters);
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Compute filtered and sorted recommendations
   */
  const filteredRecommendations = useMemo(() => {
    const filtered = applyFiltersToRecommendations(recommendations, filters);
    return sortRecommendations(filtered, sortOrder);
  }, [recommendations, filters, sortOrder]);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return (
      filters.categories.length > 0 ||
      filters.priceRange.min !== null ||
      filters.priceRange.max !== null ||
      filters.minScore !== null ||
      Object.keys(filters.attributes).length > 0
    );
  }, [filters]);

  /**
   * Context value
   */
  const contextValue: RecommendationContextValue = {
    recommendations,
    filteredRecommendations,
    allProducts,
    filters,
    sortOrder,
    loading,
    error,
    fetchRecommendations,
    applyFilter,
    updateSortOrder,
    clearFilters,
    clearError,
    hasActiveFilters,
    recommendationCount: recommendations.length,
    filteredCount: filteredRecommendations.length,
  };

  return (
    <RecommendationContext.Provider value={contextValue}>
      {children}
    </RecommendationContext.Provider>
  );
};

/**
 * Custom hook to use recommendation context
 */
export const useRecommendations = (): RecommendationContextValue => {
  const context = useContext(RecommendationContext);

  if (!context) {
    throw new Error('useRecommendations must be used within RecommendationProvider');
  }

  return context;
};
